if (!customElements.get('kit-selector')) {
  customElements.define('kit-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.querySelector('[data-results]')) return;
      this.initialized = true;
      this.steps = Object.fromEntries(Array.from(this.querySelectorAll('[data-step]')).map(step => [step.dataset.step, step]));
      this.order = Object.keys(this.steps);
      this.state = Object.fromEntries(this.order.map(key => [key, null]));
      this.resultCards = Array.from(this.querySelectorAll('[data-result-key]'));
      this.configurations = this.resultCards.map(card => Object.fromEntries(card.dataset.resultKey.split('|').map((value, index) => [this.order[index], value])));
      this.results = this.querySelector('[data-results]');
      this.summary = this.querySelector('[data-selection-summary]');
      this.status = this.querySelector('[data-configurator-status]');
      this.onClick = event => {
        const choice = event.target.closest('[data-step] [data-value]');
        if (choice && this.contains(choice) && !choice.disabled) this.select(choice.closest('[data-step]').dataset.step, choice.dataset.value);
        if (event.target.closest('[data-reset]')) this.reset();
      };
      this.onSubmit = event => {
        const card = event.target.closest('[data-result-key]');
        if (!card) return;
        this.pendingTitle = card.querySelector('.kit-alpha__result-title').textContent.trim();
        this.status.textContent = `Adding ${this.pendingTitle} to cart.`;
      };
      this.onAdded = event => {
        if (event.detail?.context !== this.dataset.eventContext) return;
        this.status.textContent = this.pendingTitle ? `${this.pendingTitle} added to cart.` : 'Selected kit added to cart.';
        this.pendingTitle = null;
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('cart:item-add', this.onAdded);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('cart:item-add', this.onAdded);
      this.initialized = false;
    }

    needsHousingStyle() {
      return Boolean(this.steps.housingStyle && (this.dataset.housingKitTypes || '').split('|').includes(this.state.kitType));
    }

    choiceAvailable(step, value) {
      const index = this.order.indexOf(step);
      return this.configurations.some(configuration => this.order.slice(0, index + 1).every(key => {
        const selected = key === step ? value : this.state[key];
        return !selected || configuration[key] === selected;
      }));
    }

    select(step, value) {
      if (!this.order.includes(step) || !this.choiceAvailable(step, value)) return;
      const index = this.order.indexOf(step);
      this.state[step] = value;
      this.order.slice(index + 1).forEach(key => { this.state[key] = null; });
      if (step === 'kitType' && this.steps.housingStyle && !this.needsHousingStyle()) this.state.housingStyle = 'standard';
      this.resetQuantities();
      this.update();
    }

    resetQuantities() {
      this.querySelectorAll('input[name="quantity"]').forEach(input => { input.value = '1'; });
    }

    update() {
      const wireRanges = [...new Set(this.configurations.map(row => row.wireRange).filter(Boolean))];
      const automaticWireRange = Boolean(this.steps.wireRange && wireRanges.length === 1 &&
        Array.from(this.steps.wireRange.querySelectorAll('[data-value]')).some(button => button.dataset.value === wireRanges[0]));
      if (automaticWireRange) this.state.wireRange = wireRanges[0];
      const isSkipped = step => (automaticWireRange && step === 'wireRange') ||
        (step === 'housingStyle' && Boolean(this.state.kitType) && !this.needsHousingStyle());
      let visibleNumber = 0;
      const instructions = this.querySelectorAll('.kit-alpha__how-to-step');
      this.order.forEach((step, index) => {
        const skipped = isSkipped(step);
        const number = skipped ? '' : ++visibleNumber;
        const badge = this.steps[step].querySelector('.kit-alpha__step-number');
        if (badge) badge.textContent = number;
        const instruction = instructions[index];
        if (instruction) {
          instruction.hidden = skipped;
          const instructionNumber = instruction.querySelector('.kit-alpha__how-to-number');
          if (instructionNumber) instructionNumber.textContent = number;
        }
      });
      this.order.forEach((step, index) => {
        const fieldset = this.steps[step];
        const skipped = isSkipped(step);
        const enabled = !skipped && this.order.slice(0, index).every(key => Boolean(this.state[key]));
        fieldset.hidden = skipped;
        fieldset.disabled = !enabled;
        fieldset.classList.toggle('is-pending', !enabled);
        fieldset.querySelector('.kit-alpha__choices')?.classList.toggle('has-selection', Boolean(this.state[step]));
        fieldset.querySelectorAll('[data-value]').forEach(button => {
          const available = this.choiceAvailable(step, button.dataset.value);
          const selected = this.state[step] === button.dataset.value;
          button.hidden = enabled && !available;
          button.disabled = !enabled || !available;
          button.setAttribute('aria-disabled', String(button.disabled));
          button.setAttribute('aria-pressed', String(selected));
          button.classList.toggle('is-selected', selected);
          button.classList.toggle('is-unavailable', enabled && !available);
        });
      });
      const platingNote = this.querySelector('[data-plating-note]');
      if (platingNote) platingNote.hidden = !this.steps.plating.querySelector('[data-value="nickel"]')?.hidden;
      const kitTypeNote = this.querySelector('[data-kit-type-note]');
      if (kitTypeNote) kitTypeNote.hidden = this.state.ways !== '2';
      this.showResult();
    }

    showResult() {
      this.resultCards.forEach(card => { card.hidden = true; });
      this.results.hidden = true;
      this.updateBootAccessory(false);
      const pending = this.order.find(key => !this.state[key]);
      if (pending) {
        const labels = {ways: 'number of ways', kitType: 'kit type', housingStyle: 'housing style', wireRange: 'wire range', plating: 'terminal plating'};
        this.status.textContent = `Select ${labels[pending] || pending}.`;
        return;
      }
      const key = this.order.map(step => this.state[step]).join('|');
      const result = this.resultCards.find(card => card.dataset.resultKey === key);
      if (!result) {
        this.status.textContent = 'That configuration is not currently available.';
        return;
      }
      result.hidden = false;
      this.results.hidden = false;
      this.summary.textContent = this.order.filter(step => step !== 'housingStyle' || this.needsHousingStyle()).map(step => this.choiceLabel(step, this.state[step])).join(' · ');
      window.KitSelectorPricing?.load(result);
      this.updateBootAccessory(true);
      const title = result.querySelector('.kit-alpha__result-title').textContent.trim();
      this.status.textContent = `Matching kit found: ${title}.`;
    }

    updateBootAccessory(hasResult) {
      const accessory = this.querySelector('[data-boot-accessory]');
      if (!accessory) return;
      const links = Array.from(accessory.querySelectorAll('[data-boot-way]'));
      links.forEach(link => { link.hidden = true; });
      const link = links.find(link => link.dataset.bootWay === this.state.ways);
      const compatible = this.needsHousingStyle() &&
        this.state.housingStyle === accessory.dataset.bootHousingStyle;
      accessory.hidden = !hasResult || !compatible || !link;
      if (!accessory.hidden) {
        link.hidden = false;
        accessory.querySelector('[data-boot-title]').textContent = `Add a ${this.state.ways}-way ${this.dataset.series} rubber boot`;
      }
    }

    reset() {
      this.state = Object.fromEntries(this.order.map(key => [key, null]));
      this.resetQuantities();
      this.update();
      this.steps.ways.querySelector('[data-value]:not([hidden])')?.focus();
    }

    choiceLabel(step, value) {
      const button = Array.from(this.steps[step].querySelectorAll('[data-value]')).find(choice => choice.dataset.value === value);
      return button?.querySelector('.kit-alpha__choice-title')?.textContent.trim() || value;
    }
  });
}
